"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

type DrawerDirection = "left" | "right" | "top" | "bottom";

export interface AppDrawerProps {
  /** Controlled open state */
  open: boolean;
  /** Called when drawer requests close */
  onClose: () => void;
  /** Slide-in direction — default "left" */
  direction?: DrawerDirection;
  /** Drawer panel width (for left/right) or height (for top/bottom) */
  size?: string | number;
  /** Overlay opacity — default "bg-black/40" */
  overlayClassName?: string;
  /** Extra classes on the panel */
  className?: string;
  children: React.ReactNode;
}

const sizeStyle = (
  direction: DrawerDirection,
  size: string | number
): React.CSSProperties => {
  const val = typeof size === "number" ? `${size}px` : size;
  if (direction === "left" || direction === "right") return { width: val };
  return { height: val };
};

const panelBase: Record<DrawerDirection, string> = {
  left:   "fixed left-0 top-0 bottom-0 z-50 flex flex-col bg-background outline-none",
  right:  "fixed right-0 top-0 bottom-0 z-50 flex flex-col bg-background outline-none",
  top:    "fixed top-0 left-0 right-0 z-50 flex flex-col bg-background outline-none",
  bottom: "fixed bottom-0 left-0 right-0 z-50 flex flex-col bg-background outline-none",
};

/**
 * AppDrawer — MUI-style reusable drawer built on vaul.
 *
 * Usage:
 * <AppDrawer open={open} onClose={() => setOpen(false)} direction="left" size={286}>
 *   ...content
 * </AppDrawer>
 */
const AppDrawer = ({
  open,
  onClose,
  direction = "left",
  size = 286,
  overlayClassName,
  className,
  children,
}: AppDrawerProps) => {
  return (
    <DrawerPrimitive.Root
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      direction={direction}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className={cn("fixed inset-0 z-50 bg-black/40", overlayClassName)}
        />
        <DrawerPrimitive.Content
          style={sizeStyle(direction, size)}
          className={cn(panelBase[direction], className)}
        >
          <VisuallyHidden.Root>
            <DrawerPrimitive.Title>Navigation</DrawerPrimitive.Title>
          </VisuallyHidden.Root>
          {children}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
};

AppDrawer.displayName = "AppDrawer";

export { AppDrawer };
